<template>
	<v-dialog
		v-model="isDialogVisible"
		persistent
		max-width="600px"
	>
		<template
			v-if="!hideActivator"
			#activator="{ on, attrs }"
		>
			<v-btn
				color="primary"
				v-bind="attrs"
				v-on="on"
			>
				{{ $t('network.wall.grants.modal.send_pixels') }}
			</v-btn>
		</template>

		<v-card>
			<v-card-title>
				<span class="headline">{{ $t('network.wall.grants.modal.title') }}</span>
			</v-card-title>

			<v-card-text>
				<!-- Origin type -->
				<v-row>
					<v-col
						cols="12"
						:md="isShowcaseOrigin ? 6 : 12"
					>
						<v-select
							v-model="form.origin"
							:items="originOptions"
							:label="$t('network.wall.grants.modal.origin_label')"
							outlined
							dense
							hide-details
							@change="onOriginChange"
						/>
					</v-col>

					<!-- Showcase event selector (only for showcase origins) -->
					<v-col
						v-if="isShowcaseOrigin"
						cols="12"
						md="6"
					>
						<v-select
							v-model="showcaseId"
							:items="showcaseOptions"
							item-text="name"
							item-value="id"
							:label="$t('network.wall.grants.modal.showcase')"
							:loading="loadingShowcases"
							outlined
							dense
							hide-details
							clearable
							@change="onShowcaseChange"
						/>
					</v-col>
				</v-row>

				<!-- Participant search for TV/Showcase -->
				<v-row v-if="form.origin !== 'manual'">
					<v-col cols="12">
						<v-autocomplete
							v-model="selectedUser"
							:items="userResults"
							:search-input.sync="userSearch"
							:loading="searchingUsers"
							item-text="display"
							item-value="email"
							:label="$t('network.wall.grants.modal.search_user')"
							:disabled="isShowcaseOrigin && !showcaseId"
							:hint="isShowcaseOrigin && !showcaseId ? $t('network.wall.grants.modal.select_showcase_first') : ''"
							persistent-hint
							outlined
							dense
							clearable
							:no-filter="form.origin !== 'influencer'"
							return-object
							@change="onUserSelected"
						/>
					</v-col>
				</v-row>

				<!-- Recipient fields -->
				<v-row>
					<v-col
						cols="12"
						md="6"
					>
						<v-text-field
							v-model="v$.form.recipient_email.$model"
							:label="$t('network.wall.grants.modal.email')"
							outlined
							dense
							hide-details
							:error="v$.form.recipient_email.$error"
							@blur="v$.form.recipient_email.$touch()"
						/>
					</v-col>
					<v-col
						cols="12"
						md="6"
					>
						<v-text-field
							v-model="form.recipient_name"
							:label="$t('network.wall.grants.modal.name')"
							outlined
							dense
							hide-details
						/>
					</v-col>
					<v-col cols="12">
						<v-text-field
							v-model="v$.form.from_name.$model"
							:label="$t('network.wall.grants.modal.from_name')"
							:placeholder="$t('network.wall.grants.modal.from_name_placeholder')"
							:error="v$.form.from_name.$error"
							outlined
							dense
							hide-details
							@blur="v$.form.from_name.$touch()"
						/>
					</v-col>
				</v-row>

				<v-row>
					<v-col
						cols="12"
						md="6"
					>
						<v-text-field
							v-model.number="v$.form.pixel_count.$model"
							:label="$t('network.wall.grants.modal.pixel_count')"
							outlined
							dense
							hide-details
							type="number"
							min="1"
							:error="v$.form.pixel_count.$error"
							@blur="v$.form.pixel_count.$touch()"
						/>
					</v-col>
					<v-col
						cols="12"
						md="6"
					>
						<v-select
							v-model="form.wall_reward_id"
							:items="rewardOptions"
							item-text="label"
							item-value="id"
							:label="$t('network.wall.grants.modal.reward_optional')"
							outlined
							dense
							hide-details
							clearable
						/>
					</v-col>
				</v-row>

				<v-row>
					<v-col
						cols="12"
						md="6"
					>
						<v-select
							v-model="form.lang"
							:items="langOptions"
							item-text="label"
							item-value="value"
							:label="$t('network.wall.grants.modal.lang')"
							outlined
							dense
							hide-details
						/>
					</v-col>
				</v-row>

				<v-row>
					<v-col cols="12">
						<v-textarea
							v-model="form.message"
							:label="$t('network.wall.grants.modal.message')"
							outlined
							dense
							hide-details
							rows="3"
						/>
					</v-col>
				</v-row>
			</v-card-text>

			<v-card-actions>
				<v-spacer />
				<v-btn
					outlined
					color="error"
					@click="close"
				>
					{{ $t('close') }}
				</v-btn>
				<v-btn
					color="success"
					:disabled="v$.form.$invalid"
					@click="send"
				>
					{{ $t('network.wall.grants.modal.send') }}
				</v-btn>
			</v-card-actions>

			<v-overlay
				:value="getGrantSaveStatus !== 'init'"
				:absolute="true"
				:opacity="'0.55'"
			>
				<v-progress-circular
					color="#ee3831"
					indeterminate
					size="64"
				/>
			</v-overlay>
		</v-card>
	</v-dialog>
</template>

<script>
import useVuelidate from '@vuelidate/core'
import { email, minValue, required } from '@vuelidate/validators'
import { mapActions, mapGetters } from 'vuex'
import { showcaseList } from '@/services/agency/showcases.service'
import { mediaPartnerApplicationList } from '@/services/agency/showcaseMediaPartnerApplications.service'
import { influencersList } from '@/services/agency/showcaseInfluencers.service'
import { submissionList } from '@/services/agency/submissions.service'

export default {
	props: {
		forceOpen: { type: Boolean, default: false },
		hideActivator: { type: Boolean, default: false },
	},
	setup() {
		return { v$: useVuelidate() }
	},
	data() {
		return {
			isDialogVisible: false,
			userSearch: '',
			userResults: [],
			searchingUsers: false,
			selectedUser: null,
			rewardOptions: [],
			showcaseId: null,
			showcaseOptions: [],
			loadingShowcases: false,
			form: {
				origin: 'manual',
				recipient_email: '',
				recipient_name: '',
				from_name: 'Mundo Gamer Network',
				pixel_count: 1,
				wall_reward_id: null,
				message: '',
				lang: 'en',
			},
			langOptions: [
				{ label: 'English', value: 'en' },
				{ label: 'Português (BR)', value: 'pt-BR' },
				{ label: 'Deutsch', value: 'de' },
				{ label: 'Español', value: 'es' },
				{ label: 'Română', value: 'ro' },
			],
			originOptions: [
				{ text: 'Manual', value: 'manual' },
				{ text: 'Showcase Game / Submission', value: 'game' },
				{ text: 'Showcase Media Partner', value: 'media_partner' },
				{ text: 'Showcase Influencer', value: 'influencer' },
				{ text: 'TV Streamer', value: 'tv_streamer' },
			],
		}
	},
	computed: {
		...mapGetters({
			getGrantSaveStatus: 'wallPixelGrants/getGrantSaveStatus',
		}),
		isShowcaseOrigin() {
			return ['game', 'media_partner', 'influencer'].includes(this.form.origin)
		},
	},
	watch: {
		forceOpen(v) {
			if (v) this.isDialogVisible = true
		},
		isDialogVisible(val) {
			if (val) {
				this.loadRewards()
				this.loadShowcases()
			} else {
				this.$emit('close')
			}
		},
		async userSearch(val) {
			if (this.form.origin === 'manual') return
			// Showcase origins need a showcase selected first
			if (this.isShowcaseOrigin && !this.showcaseId) return
			// Influencer list is fetched whole (local filter); others need a term
			if (this.form.origin !== 'influencer' && (!val || val.length < 3)) return

			this.searchingUsers = true
			try {
				if (this.form.origin === 'tv_streamer') {
					const Http = (await import('@/services/http')).default
					const res = await Http.get(`/users?filter[term]=${encodeURIComponent(val)}&per_page=15`)
					this.userResults = (res.data?.data ?? []).map(u => ({
						email: u.email,
						name: u.name,
						display: `${u.name ?? ''} <${u.email}>`,
					}))
				} else if (this.form.origin === 'media_partner') {
					const res = await mediaPartnerApplicationList({
						'filter[showcase_id]': this.showcaseId,
						'filter[term]': val,
						per_page: 20,
					})
					this.userResults = (res.data?.data ?? []).map(u => ({
						email: u.contact_email,
						name: u.contact_name,
						display: `${u.contact_name ?? ''} — ${u.outlet_or_channel_name ?? ''} <${u.contact_email}>`,
					}))
				} else if (this.form.origin === 'game') {
					const res = await submissionList({
						'filter[showcase_event_id]': this.showcaseId,
						'filter[term]': val,
						per_page: 20,
					})
					this.userResults = (res.data?.data ?? []).map(u => ({
						email: u.contact_email,
						name: u.contact_name,
						display: `${u.studio_name ?? u.game_name ?? ''} — ${u.contact_name ?? ''} <${u.contact_email}>`,
					}))
				}
			} finally {
				this.searchingUsers = false
			}
		},
	},
	methods: {
		...mapActions({
			createGrant: 'wallPixelGrants/createGrant',
		}),
		onOriginChange() {
			this.selectedUser = null
			this.userResults = []
			this.showcaseId = null
		},
		async onShowcaseChange() {
			this.selectedUser = null
			this.userResults = []
			// Influencers don't support term search → load them all for the showcase
			if (this.form.origin === 'influencer' && this.showcaseId) {
				this.searchingUsers = true
				try {
					const res = await influencersList({
						'filter[showcase_id]': this.showcaseId,
						include: 'influencer',
						per_page: 'all',
					})
					this.userResults = (res.data?.data ?? [])
						.map(row => {
							const inf = row.influencer?.data ?? row.influencer ?? {}
							const mail = inf.professional_email ?? inf.email
							if (!mail) return null
							return {
								email: mail,
								name: inf.name,
								display: `${inf.name ?? ''} <${mail}>`,
							}
						})
						.filter(Boolean)
				} finally {
					this.searchingUsers = false
				}
			}
		},
		onUserSelected(item) {
			if (!item) return
			this.form.recipient_email = item.email ?? ''
			this.form.recipient_name = item.name ?? ''
		},
		async loadShowcases() {
			if (this.showcaseOptions.length) return
			this.loadingShowcases = true
			try {
				const res = await showcaseList({ per_page: 'all', sort: 'id', order: 'desc' })
				this.showcaseOptions = res.data?.data ?? []
			} catch {
				// agency-api unreachable — showcase selection just stays empty
			} finally {
				this.loadingShowcases = false
			}
		},
		async loadRewards() {
			try {
				const Http = (await import('@/services/http')).default
				const res = await Http.get('/wall-rewards?per_page=all&filter[is_active]=1')
				this.rewardOptions = (res.data?.data ?? []).map(r => ({
					id: r.id,
					label: r.title ?? r.localized_title ?? `Reward #${r.id}`,
				}))
			} catch {}
		},
		async send() {
			this.v$.$touch()
			if (this.v$.form.$invalid) return
			try {
				await this.createGrant({
					recipient_email: this.form.recipient_email,
					recipient_name: this.form.recipient_name || undefined,
					from_name: this.form.from_name || undefined,
					pixel_count: this.form.pixel_count,
					wall_reward_id: this.form.wall_reward_id || undefined,
					message: this.form.message || undefined,
					lang: this.form.lang,
				})
				this.$emit('parent-function')
				this.close()
				this.$notify({ message: this.$t('savedSuccess'), type: 'success' })
			} catch (error) {
				const errors = Object.values(error?.response?.data?.errors ?? {}).flat()
				errors.forEach(msg => this.$notify({ message: msg, type: 'danger' }))
			}
		},
		close() {
			this.isDialogVisible = false
			this.v$.$reset()
			this.form = { origin: 'manual', recipient_email: '', recipient_name: '', from_name: 'Mundo Gamer Network', pixel_count: 1, wall_reward_id: null, message: '', lang: 'en' }
			this.selectedUser = null
			this.showcaseId = null
			this.userResults = []
		},
	},
	validations() {
		return {
			form: {
				recipient_email: { required, email },
				pixel_count: { required, minValue: minValue(1) },
				from_name: { required },
			},
		}
	},
}
</script>
